(() => {
  'use strict';
  if (window.NiralaStoreHours) return;

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const weekdayIndex = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const controlSelector = '[data-add-to-cart], [data-quick-add], button[name="checkout"], input[name="checkout"], form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"], a[href*="/checkout"]';
  let current = null;

  const parseTime = (value) => {
    const match = /^(\d{2}):(\d{2})$/.exec(String(value || '').trim());
    if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  };

  const isCheckoutLink = (element) => {
    if (element.tagName !== 'A') return false;
    try {
      const url = new URL(element.href, window.location.href);
      return url.origin === window.location.origin && /\/checkout(?:\/|$)/.test(url.pathname);
    } catch (_) { return false; }
  };

  const isAddForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.matches('[data-product-form], [data-quick-order-form]')) return true;
    try { return /\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action, window.location.href).pathname); }
    catch (_) { return false; }
  };

  class StoreHours {
    constructor(root) {
      this.root = root;
      this.banner = root.querySelector('[data-store-hours-banner]');
      this.message = root.querySelector('[data-store-hours-message]');
      this.notice = root.querySelector('[data-store-hours-notice]');
      this.warning = root.querySelector('[data-store-hours-editor-warning]');
      this.editorNote = root.querySelector('[data-store-hours-editor-note]');
      this.warnings = [];
      this.decorated = new Map();
      this.days = new Map();
      this.previousState = '';
      this.blocked = false;
      try {
        this.config = JSON.parse(root.querySelector('[data-store-hours-config]').textContent);
        if (!this.config || typeof this.config !== 'object' || Array.isArray(this.config)) throw new Error('Invalid settings');
      } catch (_) {
        this.config = { enabled: true, mode: 'closed', timezone: 'Asia/Karachi' };
        this.warnings.push('Opening hours could not be read. Check and save this section again.');
      }
      this.enabled = this.config.enabled === true;
      this.designMode = this.config.designMode === true || window.Shopify?.designMode === true;
      this.mode = ['auto', 'open', 'closed'].includes(this.config.mode) ? this.config.mode : 'auto';
      if (this.mode !== this.config.mode) this.warnings.push('Unknown ordering mode: the automatic schedule is being used.');
      this.timezone = String(this.config.timezone || '').trim();
      const makeFormatter = (timeZone) => new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
      try {
        if (!this.timezone) throw new Error('Missing time zone');
        this.formatter = makeFormatter(this.timezone);
      } catch (_) {
        this.timezone = 'Asia/Karachi';
        this.warnings.push('Invalid time zone. Asia/Karachi is being used.');
        try { this.formatter = makeFormatter(this.timezone); } catch (_) { this.formatter = null; }
      }
      this.daily = this.compileDay(this.config, 'Daily schedule');
      for (const definition of Array.isArray(this.config.days) ? this.config.days : []) {
        if (!definition || typeof definition !== 'object') {
          this.warnings.push('An invalid day override is ignored.');
          continue;
        }
        const day = Number(definition.weekday);
        if (!Number.isInteger(day) || day < 0 || day > 6 || definition.weekday === '' || definition.weekday == null) {
          this.warnings.push('A day override has an invalid weekday and is ignored.');
          continue;
        }
        if (this.days.has(day)) {
          this.warnings.push(`${dayNames[day]} appears more than once. Only its first block is used.`);
          continue;
        }
        this.days.set(day, this.compileDay(definition, dayNames[day]));
      }
      this.scheduleRefresh();
      this.observer = new MutationObserver(() => { if (this.blocked) this.decorate(); });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    scheduleRefresh() {
      window.clearTimeout(this.timer);
      // Align the banner to the next minute boundary instead of waiting a full
      // minute from when the visitor happened to open the page.
      const delay = 60000 - (Date.now() % 60000) + 25;
      this.timer = window.setTimeout(() => {
        this.refresh();
        this.scheduleRefresh();
      }, delay);
    }

    compileDay(definition, label) {
      if (definition.closed === true) return { closed: true };
      if (definition.allDay === true) return { allDay: true };
      const open = parseTime(definition.openingTime);
      const close = parseTime(definition.closingTime);
      if (open === null || close === null || open === close) {
        this.warnings.push(`${label}: enter different HH:MM opening and closing times, or select Open 24 hours. This interval is closed until corrected.`);
        return { closed: true };
      }
      return { open, close };
    }

    localClock() {
      const now = new Date();
      if (this.formatter) {
        try {
          const parts = Object.fromEntries(this.formatter.formatToParts(now).map((part) => [part.type, part.value]));
          const day = weekdayIndex[parts.weekday];
          const minute = (Number(parts.hour) % 24) * 60 + Number(parts.minute);
          if (day !== undefined && Number.isFinite(minute)) return { day, minute };
        } catch (_) { /* Use the documented Karachi fallback below. */ }
      }
      // Used only if Intl cannot format the configured or fallback time zone.
      const karachi = new Date(now.getTime() + 5 * 60 * 60 * 1000);
      return { day: karachi.getUTCDay(), minute: karachi.getUTCHours() * 60 + karachi.getUTCMinutes() };
    }

    isOpen() {
      if (!this.enabled || this.mode === 'open') return true;
      if (this.mode === 'closed') return false;
      const { day, minute } = this.localClock();
      const today = this.days.get(day) || this.daily;
      const yesterday = this.days.get((day + 6) % 7) || this.daily;
      if (!today.closed) {
        if (today.allDay) return true;
        if (today.open < today.close && minute >= today.open && minute < today.close) return true;
        if (today.open > today.close && minute >= today.open) return true;
      }
      return !yesterday.closed && !yesterday.allDay && yesterday.open > yesterday.close && minute < yesterday.close;
    }

    canOrder() { return this.designMode || this.isOpen(); }

    nextOpening() {
      if (!this.enabled || this.mode !== 'auto' || this.isOpen()) return null;
      const { day, minute } = this.localClock();
      // A weekly schedule can next open later today or on the same weekday next
      // week. A closed day never creates an opening, even after overnight hours.
      for (let offset = 0; offset <= 7; offset += 1) {
        const weekday = (day + offset) % 7;
        const schedule = this.days.get(weekday) || this.daily;
        if (schedule.closed) continue;
        const opening = schedule.allDay ? 0 : schedule.open;
        if (!Number.isFinite(opening) || (offset === 0 && opening <= minute)) continue;
        return { day: weekday, daysAway: offset, minute: opening };
      }
      return null;
    }

    openingTimeText(minute) {
      const hour = Math.floor(minute / 60);
      const minutes = String(minute % 60).padStart(2, '0');
      if (this.config.timeFormat === '24h') return `${String(hour).padStart(2, '0')}:${minutes}`;
      return `${hour % 12 || 12}:${minutes}${hour >= 12 ? 'PM' : 'AM'}`;
    }

    closedMessage() {
      const message = String(this.config.closedMessage || 'Sorry, we are closed right now.').trim();
      if (this.config.showNextOpening === false || this.mode !== 'auto') return message;
      const next = this.nextOpening();
      if (!next) return message;
      const prefix = String(this.config.reopeningMessage || 'We will re-open').trim();
      const at = String(this.config.reopeningAt || 'at').trim();
      const day = next.daysAway === 0 ? '' : next.daysAway === 1 ? String(this.config.tomorrowLabel || 'tomorrow').trim() : dayNames[next.day];
      const separator = /[.!?。！？]$/.test(message) ? ' ' : '. ';
      return `${message}${separator}${[prefix, day, at, this.openingTimeText(next.minute)].filter(Boolean).join(' ')}.`;
    }

    refresh() {
      const open = this.isOpen();
      this.blocked = !this.designMode && !open;
      const text = open ? String(this.config.openMessage || 'We’re open — order your favourites.') : this.closedMessage();
      if (this.message && this.message.textContent !== text) this.message.textContent = text;
      if (this.banner) {
        this.banner.hidden = !this.enabled || (open && !this.config.showOpenBar);
        this.banner.dataset.state = open ? 'open' : 'closed';
      }
      if (this.editorNote) this.editorNote.textContent = this.enabled ? `Store hours preview: ${open ? 'open' : 'closed'} (${this.timezone}). Ordering remains available in the Theme Editor.` : 'Opening hours are disabled. Enable them after setting your daily schedule or day overrides.';
      if (this.warning) {
        this.warning.hidden = this.warnings.length === 0;
        this.warning.textContent = this.warnings.join(' ');
      }
      document.documentElement.classList.toggle('np-store-is-closed', this.blocked);
      this.decorate();
      if (!this.blocked && this.notice) this.notice.hidden = true;
      const state = `${this.enabled}|${open}|${this.blocked}`;
      if (state !== this.previousState) {
        this.previousState = state;
        document.dispatchEvent(new CustomEvent('nirala:hours-changed', { detail: { enabled: this.enabled, open, canOrder: !this.blocked, mode: this.mode, timezone: this.timezone } }));
      }
    }

    decorate() {
      for (const [element, previous] of this.decorated) {
        if (!this.blocked || !element.isConnected) {
          if (previous === null) element.removeAttribute('aria-disabled');
          else element.setAttribute('aria-disabled', previous);
          element.classList.remove('np-store-order-blocked');
          this.decorated.delete(element);
        }
      }
      if (!this.blocked) return;
      for (const element of document.querySelectorAll(controlSelector)) {
        if (element.tagName === 'A' && !isCheckoutLink(element)) continue;
        if (!this.decorated.has(element)) this.decorated.set(element, element.getAttribute('aria-disabled'));
        element.setAttribute('aria-disabled', 'true');
        element.classList.add('np-store-order-blocked');
      }
    }

    check() {
      this.refresh();
      if (!this.blocked) return true;
      if (this.notice) {
        const dialogs = [...document.querySelectorAll('dialog[open]')];
        const target = dialogs.at(-1) || this.root;
        if (this.notice.parentElement !== target) target.appendChild(this.notice);
        this.notice.textContent = this.closedMessage();
        this.notice.hidden = false;
        window.clearTimeout(this.noticeTimer);
        this.noticeTimer = window.setTimeout(() => { this.notice.hidden = true; }, 6000);
      } else if (window.Nirala?.tell) window.Nirala.tell(this.closedMessage());
      return false;
    }

    destroy() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.noticeTimer);
      this.observer.disconnect();
      this.blocked = false;
      this.decorate();
      document.documentElement.classList.remove('np-store-is-closed');
      if (this.notice && this.notice.parentElement !== this.root) this.notice.remove();
    }
  }

  const init = () => {
    const root = document.querySelector('[data-store-hours]');
    if (!root || current?.root === root) { current?.refresh(); return; }
    current?.destroy();
    current = new StoreHours(root);
    current.refresh();
  };

  window.NiralaStoreHours = {
    canOrder: () => current?.canOrder() ?? true,
    check: () => current?.check() ?? true
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.(controlSelector);
    if (!control || (control.tagName === 'A' && !isCheckoutLink(control))) return;
    if (current && !current.check()) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', (event) => {
    if (!isAddForm(event.target) && event.submitter?.name !== 'checkout') return;
    if (current && !current.check()) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) current?.refresh(); });
  document.addEventListener('nirala:cart-updated', () => current?.refresh());
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    if (current && event.target.contains(current.root)) { current.destroy(); current = null; }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
